/** Small DOM helpers shared by the home page and the meeting. */

import { t } from './i18n';
import { icons } from './icons';

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export function h(tag, props = {}, ...children) {
	const node = document.createElement(tag);
	for (const [k, v] of Object.entries(props || {})) {
		if (v === undefined || v === null || v === false) continue;
		if (k === 'class') node.className = v;
		else if (k === 'html') node.innerHTML = v;
		else if (k === 'dataset') Object.assign(node.dataset, v);
		else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
		else if (k in node && k !== 'list' && k !== 'form') node[k] = v;
		else node.setAttribute(k, v === true ? '' : v);
	}
	for (const c of children.flat()) {
		if (c === null || c === undefined || c === false) continue;
		node.append(c instanceof Node ? c : document.createTextNode(String(c)));
	}
	return node;
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function initials(name) {
	const parts = String(name || '?').trim().split(/\s+/);
	return ((parts[0] || '?')[0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function hueFor(id) {
	let x = 0;
	for (const ch of String(id)) x = (x * 31 + ch.charCodeAt(0)) >>> 0;
	return x % 360;
}

export function avatar(peer, cls = 'avatar') {
	return h('div', { class: cls, style: `--hue:${hueFor(peer.id)}` }, initials(peer.name));
}

export function formatClock(ms) {
	const s = Math.max(0, Math.floor(ms / 1000));
	const hh = Math.floor(s / 3600);
	const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
	const ss = String(s % 60).padStart(2, '0');
	return hh ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function formatBytes(n) {
	if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
	if (n >= 1024) return `${Math.round(n / 1024)} KB`;
	return `${n} B`;
}

export async function copyText(text) {
	try {
		await navigator.clipboard.writeText(text);
	} catch {
		const ta = h('textarea', { style: 'position:fixed;opacity:0' }, text);
		document.body.append(ta);
		ta.select();
		try { document.execCommand('copy'); } catch { /* ignore */ }
		ta.remove();
	}
	toast(t('toast.copied'), { type: 'success', duration: 2000 });
}

/* ---------------------------------------------------------------------- */
/* Toasts                                                                 */
/* ---------------------------------------------------------------------- */

export function toast(text, { type = 'info', action = null, action2 = null, duration = 5000, icon = null } = {}) {
	let box = $('#toasts');
	if (!box) {
		box = h('div', { id: 'toasts', class: 'toasts', 'aria-live': 'polite' });
		document.body.append(box);
	}
	const node = h('div', { class: `toast toast-${type}` },
		icon ? h('span', { class: 'toast-icon', html: icon }) : null,
		h('span', { class: 'toast-text' }, text));
	for (const a of [action, action2].filter(Boolean)) {
		node.append(h('button', { class: `btn btn-sm ${a.primary === false ? '' : 'btn-accent'}`, onclick: () => { node.remove(); a.run(); } }, a.label));
	}
	node.append(h('button', { class: 'toast-x', title: t('common.close'), html: icons.close, onclick: () => node.remove() }));
	box.append(node);
	while (box.children.length > 4) box.firstChild.remove();
	if (duration) setTimeout(() => node.remove(), duration);
	return node;
}

/* ---------------------------------------------------------------------- */
/* Dialogs (translatable replacements for alert / confirm / prompt)       */
/* ---------------------------------------------------------------------- */

export function openDialog({ title, body, actions = [], wide = false, onClose = null }) {
	const dlg = h('dialog', { class: `dialog${wide ? ' dialog-wide' : ''}` });
	const close = () => {
		dlg.close();
		dlg.remove();
		if (onClose) onClose();
	};
	dlg.append(
		h('div', { class: 'dialog-head' },
			h('h2', {}, title),
			h('button', { class: 'icon-btn', title: t('common.close'), html: icons.close, onclick: close })),
		h('div', { class: 'dialog-body' }, body),
		actions.length ? h('div', { class: 'dialog-actions' }, ...actions.map((a) => h('button', {
			class: `btn ${a.kind ? `btn-${a.kind}` : ''}`,
			onclick: async () => {
				if (a.run && (await a.run()) === false) return;
				close();
			}
		}, a.label))) : null
	);
	dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
	document.body.append(dlg);
	dlg.showModal();
	return { dlg, close };
}

export function confirmDialog(title, text, { ok = t('common.yes'), danger = false } = {}) {
	return new Promise((resolve) => {
		let answered = false;
		openDialog({
			title,
			body: h('p', { class: 'muted' }, text),
			actions: [
				{ label: t('common.cancel'), run: () => { answered = true; resolve(false); } },
				{ label: ok, kind: danger ? 'danger' : 'accent', run: () => { answered = true; resolve(true); } }
			],
			onClose: () => { if (!answered) resolve(false); }
		});
	});
}

export function promptDialog(title, value = '', { max = 60 } = {}) {
	return new Promise((resolve) => {
		let answered = false;
		const input = h('input', { class: 'input', value, maxLength: max });
		const { close } = openDialog({
			title,
			body: input,
			actions: [
				{ label: t('common.cancel'), run: () => { answered = true; resolve(null); } },
				{ label: t('common.save'), kind: 'accent', run: () => { answered = true; resolve(input.value.trim() || null); } }
			],
			onClose: () => { if (!answered) resolve(null); }
		});
		input.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				answered = true;
				resolve(input.value.trim() || null);
				close();
			}
		});
		setTimeout(() => { input.focus(); input.select(); }, 30);
	});
}

/* ---------------------------------------------------------------------- */
/* Popover menus                                                          */
/* ---------------------------------------------------------------------- */

let openMenuEl = null;

export function closeMenu() {
	if (openMenuEl) openMenuEl.remove();
	openMenuEl = null;
	document.removeEventListener('pointerdown', outside, true);
}

function outside(e) {
	if (openMenuEl && !openMenuEl.contains(e.target)) closeMenu();
}

/** items: [{ label, icon?, run, danger?, checked? } | 'sep' | { title }] or a ready element */
export function openMenu(anchor, items, { side = 'auto', cls = '' } = {}) {
	closeMenu();
	const menu = h('div', { class: `menu ${cls}`, role: 'menu' });
	if (items instanceof Node) {
		menu.append(items);
	} else {
		for (const it of items) {
			if (it === 'sep') menu.append(h('div', { class: 'menu-sep' }));
			else if (it.title) menu.append(h('div', { class: 'menu-title' }, it.title));
			else {
				menu.append(h('button', {
					class: `menu-item${it.danger ? ' danger' : ''}${it.checked ? ' checked' : ''}`,
					role: 'menuitem',
					disabled: it.disabled,
					onclick: () => { closeMenu(); it.run(); }
				}, it.icon ? h('span', { class: 'menu-icon', html: it.icon }) : null, h('span', {}, it.label)));
			}
		}
	}
	document.body.append(menu);
	const r = anchor.getBoundingClientRect();
	const mw = menu.offsetWidth;
	const mh = menu.offsetHeight;
	let left;
	let top;
	if (side === 'right' || (side === 'auto' && r.right + mw + 12 < innerWidth && r.left < 120)) {
		left = r.right + 10;
		top = Math.min(r.top, innerHeight - mh - 8);
	} else {
		left = Math.min(Math.max(8, r.right - mw), innerWidth - mw - 8);
		top = r.bottom + 6;
		if (top + mh > innerHeight - 8) top = Math.max(8, r.top - mh - 6);
	}
	menu.style.left = `${Math.max(8, left)}px`;
	menu.style.top = `${Math.max(8, top)}px`;
	openMenuEl = menu;
	setTimeout(() => document.addEventListener('pointerdown', outside, true), 0);
	return menu;
}
